/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Flex, Spinner, Stack, Text } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';

import { CheckCircle, File, UploadSimple, X } from '@phosphor-icons/react';

import { fetchAPI } from '@/api/fetch';
import {
  apiExtractQuestionBank,
  ExtractQuestionBankPayload
} from '@/api/services/question_bank.service';
import { apiGetExtractorProviders } from '@/api/services';
import { AnalysisProvider } from '@/types/AnalysisProvider';
import { LlmModelProvider, PromptKey, getLlmModelProviderSupportFileTypes } from '@/types/enums';

import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle
} from '@/components/ui/dialog';
import { getFileSize } from '@/utils';
import { MAX_RESUME_FILE_SIZE, MAX_RESUME_FILE_SIZE_MB } from '@/config/constants';

interface Provider {
  analysis_id: string | null;
  analysis_name: PromptKey;
  provider: LlmModelProvider | null;
  loading: boolean;
  error: string | null;
}

interface ImportQuestionBankDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (data: QuestionBankExtractionData) => void;
}

export default function ImportQuestionBankDialog({
  isOpen,
  onClose,
  onImport
}: ImportQuestionBankDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [loadingText, setLoadingText] = useState('');
  const [provider, setProvider] = useState<Provider>({
    analysis_id: null,
    analysis_name: PromptKey.JDExtractor, // Using JDExtractor as it handles document extraction
    provider: null,
    loading: true,
    error: null
  });
  const fileSizeLimit = MAX_RESUME_FILE_SIZE;

  const defaultProvider: Provider = {
    analysis_id: null,
    analysis_name: PromptKey.JDExtractor,
    provider: null,
    loading: false,
    error: null
  };

  const fetchProviders = useCallback(async () => {
    setProvider({ ...defaultProvider, loading: true, error: null });

    try {
      const response = await fetchAPI<AnalysisProvider[]>({
        request: apiGetExtractorProviders
      });

      if (response.success) {
        const providersData = response.data;
        const extractorProvider = providersData.find(
          (p) => p.analysis_name === PromptKey.JDExtractor
        );

        if (extractorProvider) {
          setProvider({
            ...extractorProvider,
            loading: false,
            error: null
          });
        } else {
          console.error('No extractor provider configured.');
          setProvider({
            ...defaultProvider,
            error: 'No extractor provider configured. Please contact support to enable file upload.'
          });
        }
      } else {
        console.error('Error fetching analysis providers:', response.message);
        setProvider({
          ...defaultProvider,
          error: 'Failed to load file upload options. Please try again later.'
        });
      }
    } catch (error) {
      console.error('Unexpected error fetching analysis providers:', error);
      setProvider({
        ...defaultProvider,
        error: 'Failed to load file upload options. Please try again later.'
      });
    }
  }, []);

  const retryProviderFetch = useCallback(() => {
    fetchProviders();
  }, [fetchProviders]);

  // Initialize providers when dialog opens
  useEffect(() => {
    if (isOpen) {
      fetchProviders();
    }
  }, [isOpen, fetchProviders]);

  useEffect(() => {
    if (!isUploading) return;

    const baseMessages = ['Uploading', 'Processing', 'Extracting', 'Analyzing'];
    let index = 0;
    let dotCount = 0;
    const maxDots = 3;
    const maxRepeats = 4;
    let repeatCount = 0;

    const updateLoadingText = () => {
      setLoadingText(`${baseMessages[index]}${'.'.repeat(dotCount)}`);
      dotCount = (dotCount + 1) % (maxDots + 1);
      if (dotCount === 0 && ++repeatCount === maxRepeats) {
        index = (index + 1) % baseMessages.length;
        repeatCount = 0;
      }
    };

    const intervalId = setInterval(updateLoadingText, 500);
    updateLoadingText();

    return () => clearInterval(intervalId);
  }, [isUploading]);

  const handleClose = () => {
    if (!isUploading && !provider.loading) {
      setFile(null);
      setFileError(null);
      onClose();
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (provider?.error || provider?.loading) {
      setFileError('File upload is not available. Please wait or try again later.');
      return;
    }

    if (selectedFile.size > fileSizeLimit) {
      setFileError(`File size exceeds ${MAX_RESUME_FILE_SIZE_MB} MB`);
      return;
    }

    setFile(selectedFile);
    setFileError(null);
  };

  const handleImport = async () => {
    if (!file) {
      setFileError('Please select a file to import');
      return;
    }

    setIsUploading(true);
    setFileError(null);

    try {
      const payload: ExtractQuestionBankPayload = { file };
      const response = await fetchAPI<QuestionBankExtractionData>({
        request: () => apiExtractQuestionBank({ payload })
      });

      if (response.success && response.data) {
        onImport(response.data);
        handleClose();
      } else {
        console.error('Error extracting question bank:', response.message);
        setFileError('File extraction failed. Please try again.');
      }
    } catch (error) {
      console.error('Error extracting question bank:', error);
      setFileError('File extraction failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <DialogRoot size="lg" open={isOpen} onOpenChange={(e) => !e.open && handleClose()}>
      <DialogContent maxW="600px" borderRadius="12px">
        <DialogHeader>
          <DialogTitle fontSize="20px" fontWeight="bold" color="neutrals.900">
            Import Question Bank
          </DialogTitle>
          {!isUploading && !provider.loading && (
            <DialogCloseTrigger onClick={handleClose}>
              <X size={20} />
            </DialogCloseTrigger>
          )}
        </DialogHeader>

        <DialogBody>
          {provider.loading ? (
            <Box
              w="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              py="80px"
            >
              <Stack align="center">
                <Spinner
                  w="50px"
                  h="50px"
                  borderWidth="5px"
                  color="primary.900"
                  css={{ '--spinner-track-color': 'colors.gray.200' }}
                  mb="16px"
                />
                <Text fontSize="18px" fontWeight="bold" color="neutrals.900">
                  Loading...
                </Text>
                <Text fontSize="14px" color="neutrals.600">
                  Configuring question bank upload settings
                </Text>
              </Stack>
            </Box>
          ) : (
            <Stack gap="24px">
              <Box>
                <Text fontSize="16px" color="neutrals.700" mb="8px">
                  Upload your question bank file, and we'll fill in the details for you.
                </Text>
                {(() => {
                  const fileTypes = getLlmModelProviderSupportFileTypes(provider?.provider);
                  const isSingleType = fileTypes.length === 1;
                  return (
                    <Text fontSize="16px" color="neutrals.500">
                      Supported {isSingleType ? 'format' : 'formats'}: {fileTypes.join(', ')} (
                      {getFileSize(fileSizeLimit)} max)
                    </Text>
                  );
                })()}
              </Box>

              {provider?.error ? (
                <Box
                  w="100%"
                  border="2px solid"
                  borderRadius="12px"
                  borderColor="error.300"
                  bg="error.50"
                  p="24px"
                  textAlign="center"
                  mt="16px"
                >
                  <Text fontSize="18px" fontWeight="bold" color="error.700" mb="8px">
                    Upload Unavailable
                  </Text>
                  <Text fontSize="16px" color="error.600" mb="24px">
                    {provider.error}
                  </Text>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={retryProviderFetch}
                    color="neutrals.700"
                    fontSize="16px"
                    borderColor="neutrals.500"
                    borderRadius="8px"
                    p="8px 24px"
                  >
                    Retry
                  </Button>
                </Box>
              ) : file || isUploading ? (
                <Box display="flex" alignItems="center" width="100%">
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    width="120px"
                    height="120px"
                    borderRadius="12px"
                    border="1px solid"
                    borderColor={fileError ? 'error.500' : 'primary.100'}
                    bg="primary.50"
                  >
                    {isUploading ? (
                      <>
                        <Spinner
                          size="lg"
                          borderWidth="4px"
                          color="primary.900"
                          css={{ '--spinner-track-color': 'colors.gray.200' }}
                        />
                        <Text
                          mt="8px"
                          fontSize="12px"
                          fontWeight="semibold"
                          color="neutrals.900"
                          textAlign="center"
                        >
                          {loadingText}
                        </Text>
                      </>
                    ) : (
                      <File size={48} color="#4e76ff" />
                    )}
                  </Box>
                  <Box ml="24px" color="neutrals.900">
                    <Box display="flex" alignItems="center" mb="4px">
                      {!isUploading && !provider.loading && (
                        <Box mr="12px">
                          <CheckCircle size={20} color="#4e76ff" weight="fill" />
                        </Box>
                      )}
                      <Text fontSize="16px" color="neutrals.800" mr="16px">
                        {file?.name}
                      </Text>
                      {!isUploading && !provider.loading && (
                        <X
                          size={16}
                          color="#545454"
                          onClick={() => {
                            setFile(null);
                            setFileError(null);
                          }}
                          cursor="pointer"
                        />
                      )}
                    </Box>
                    <Text fontSize="14px" color={fileError ? 'error.700' : 'neutrals.500'}>
                      {getFileSize(file?.size || 0)}
                    </Text>
                    {fileError && (
                      <Text fontSize="14px" color="error.700" mt="4px">
                        {fileError}
                      </Text>
                    )}
                  </Box>
                </Box>
              ) : (
                <Box
                  position="relative"
                  width="100%"
                  height="150px"
                  border="2px dashed"
                  borderColor="neutrals.300"
                  borderRadius="12px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  cursor={provider?.error ? 'not-allowed' : 'pointer'}
                  _hover={{ borderColor: 'neutrals.400' }}
                  bg="neutrals.50"
                >
                  <input
                    type="file"
                    accept={
                      provider?.provider
                        ? getLlmModelProviderSupportFileTypes(provider.provider).join(',')
                        : '.pdf,.txt'
                    }
                    disabled={!!provider?.error}
                    style={{
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      opacity: 0,
                      cursor: provider?.error ? 'not-allowed' : 'pointer'
                    }}
                    onChange={handleFileUpload}
                  />
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <UploadSimple size={48} color="#404040" />
                    <Text textAlign="center" color="neutrals.900" mt="8px" fontSize="14px">
                      Drop file here or{' '}
                      <Box as="span" color="primary.900">
                        click to upload
                      </Box>
                    </Text>
                  </Box>
                </Box>
              )}
            </Stack>
          )}
        </DialogBody>

        <DialogFooter>
          <Flex gap="12px">
            <DialogActionTrigger asChild>
              <Button
                variant="outline"
                border="1px solid"
                borderColor="primary.900"
                color="primary.900"
                bg="neutrals.0"
                borderRadius="8px"
                fontSize="16px"
                w="100px"
                _hover={{ bg: 'neutrals.100' }}
                disabled={isUploading || provider.loading}
                onClick={handleClose}
              >
                Cancel
              </Button>
            </DialogActionTrigger>
            <Button
              borderRadius="8px"
              bg="primary.900"
              color="neutrals.0"
              fontSize="16px"
              w="100px"
              _hover={{ bg: 'primary.700' }}
              loading={isUploading}
              disabled={!file || !!provider?.error}
              onClick={handleImport}
            >
              Import
            </Button>
          </Flex>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
